import { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  TrendingUp,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  CreditCard,
  Activity,
  Globe,
  Code,
  AlertCircle,
  Server,
  DollarSign,
  HandCoins,
  MapPin,
  Wifi,
  Database,
  Video,
  Layers,
  ListTodo,
  ShoppingCart,
  Lightbulb,
  FileText,
  FileCode,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'

export default function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const [isMaster, setIsMaster] = useState(localStorage.getItem('master') === 'master')
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    // 检查登录状态
    const token = localStorage.getItem('token')
    if (!token) {
      navigate('/admin/login')
    }
  }, [navigate])

  const frequentItems: Array<{
    icon: LucideIcon
    label: string
    path: string
    external?: boolean
  }> = [
    { icon: TrendingUp, label: 'Trend', path: '/admin/trend' },
    { icon: Activity, label: 'Task', path: '/admin/task' },
    { icon: ListTodo, label: 'Task Manage', path: '/admin/taskmanage' },
    { icon: Code, label: 'Flow', path: '/admin/flow' },
    { icon: HandCoins, label: 'Withdrawal', path: '/admin/withdrawal' },
    { icon: Settings, label: 'Dev', path: '/admin/dev' },
    { icon: HandCoins, label: 'Recharge', path: '/admin/recharge' },
    { icon: Wifi, label: 'IP Pool', path: '/admin/ippool' },
    { icon: Database, label: 'IP Cache', path: '/admin/ipcache' },
    { icon: Lightbulb, label: 'Strategy', path: '/admin/strategy' },
  ]

  const otherItems: Array<{
    icon: LucideIcon
    label: string
    path: string
    external?: boolean
  }> = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: Layers, label: 'Block', path: '/admin/block' },
    { icon: Globe, label: 'Plat', path: '/admin/plat' },
    { icon: Users, label: 'Account', path: '/admin/account' },
    { icon: Code, label: 'Interface', path: '/admin/interface' },
    { icon: FileCode, label: 'JS Dev', path: '/admin/jsdev' },
    { icon: Code, label: 'APIs', path: '/admin/apis' },
    { icon: Activity, label: 'API Logs', path: '/admin/apilogs' },
    { icon: FileText, label: 'Login Log', path: '/admin/loginlog' },
    { icon: Video, label: 'Live', path: '/living', external: true },
    { icon: Users, label: 'User', path: '/admin/user' },
    { icon: CreditCard, label: 'Device', path: '/admin/device' },
    { icon: ShoppingCart, label: 'Order', path: '/admin/order' },
    { icon: AlertCircle, label: 'Error', path: '/admin/error' },
    { icon: Server, label: 'Node', path: '/admin/node' },
    { icon: DollarSign, label: 'Cost', path: '/admin/cost' },
    { icon: MapPin, label: 'Addresses', path: '/admin/addresses' },
  ]

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('master')
    navigate('/admin/login')
  }

  const handleMasterToggle = (checked: boolean) => {
    if (checked) {
      localStorage.setItem('master', 'master')
    } else {
      localStorage.removeItem('master')
    }
    setIsMaster(checked)
  }

  return (
    <div className="flex h-screen bg-background">
      {/* 侧边栏 */}
      <aside
        className={`${
          collapsed ? 'w-16' : 'w-64'
        } bg-card border-r border-border transition-all duration-300 flex flex-col shadow-lg overflow-hidden`}
      >
        {/* Logo 和折叠按钮 */}
        <div className="h-16 flex items-center justify-between px-3 border-b border-border bg-card/50 backdrop-blur-sm flex-shrink-0">
          {!collapsed && (
            <h1 className="text-lg font-bold bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent truncate">
              Admin
            </h1>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="ml-auto hover:bg-accent flex-shrink-0"
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Master 模式切换 */}
        <div className="px-3 py-3 border-b border-border bg-card/30 flex-shrink-0">
          <div className={`flex items-center gap-2 ${collapsed ? 'justify-center' : 'justify-between'}`}>
            {!collapsed && (
              <Label htmlFor="master-mode" className="text-xs font-medium cursor-pointer">
                Master 模式
              </Label>
            )}
            <Switch
              id="master-mode"
              checked={isMaster}
              onCheckedChange={handleMasterToggle}
              className="data-[state=checked]:bg-primary"
            />
          </div>
          {!collapsed && (
            <div className="mt-1 text-[10px] text-muted-foreground">
              {isMaster ? 'VITE_PROXY_URL' : 'VITE_ALANT_URL'}
            </div>
          )}
        </div>

        {/* 菜单项 */}
        <nav className="flex-1 py-2 overflow-y-auto overflow-x-hidden scrollbar-thin">
          <div className="space-y-0.5 px-2">
            {/* 常用功能分组 */}
            {!collapsed && (
              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                常用
              </div>
            )}
            {frequentItems.map((item) => {
              const isActive = location.pathname === item.path
              return (
                <Button
                  key={item.path}
                  variant="ghost"
                  className={`w-full justify-start h-10 transition-colors ${
                    collapsed ? 'px-0 justify-center' : 'px-3'
                  } ${
                    isActive
                      ? 'bg-primary/15 text-primary font-medium border-l-2 border-primary'
                      : 'hover:bg-accent/50'
                  }`}
                  onClick={() => {
                    if (item.external) {
                      window.open(item.path, '_blank')
                    } else {
                      navigate(item.path)
                    }
                  }}
                >
                  <item.icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-primary' : ''}`} />
                  {!collapsed && (
                    <span className={`ml-3 text-sm truncate ${isActive ? 'font-medium' : ''}`}>
                      {item.label}
                    </span>
                  )}
                </Button>
              )
            })}

            {/* 分割线 */}
            <div className="my-3 border-t border-border/50" />

            {/* 其他功能分组 */}
            {!collapsed && (
              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                其他
              </div>
            )}
            {otherItems.map((item) => {
              const isActive = location.pathname === item.path
              return (
                <Button
                  key={item.path}
                  variant="ghost"
                  className={`w-full justify-start h-10 transition-colors ${
                    collapsed ? 'px-0 justify-center' : 'px-3'
                  } ${
                    isActive
                      ? 'bg-primary/15 text-primary font-medium border-l-2 border-primary'
                      : 'hover:bg-accent/50'
                  }`}
                  onClick={() => {
                    if (item.external) {
                      window.open(item.path, '_blank')
                    } else {
                      navigate(item.path)
                    }
                  }}
                >
                  <item.icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-primary' : ''}`} />
                  {!collapsed && (
                    <span className={`ml-3 text-sm truncate ${isActive ? 'font-medium' : ''}`}>
                      {item.label}
                    </span>
                  )}
                </Button>
              )
            })}
          </div>
        </nav>

        {/* 底部操作区 */}
        <div className="border-t border-border p-2 bg-card/50 backdrop-blur-sm flex-shrink-0">
          <Button
            variant="ghost"
            className={`w-full justify-start h-10 text-destructive hover:bg-destructive/10 hover:text-destructive ${
              collapsed ? 'px-0 justify-center' : 'px-3'
            }`}
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span className="ml-3 text-sm">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* 主内容区 */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  )
}
